import { Component, ElementRef, OnDestroy, OnInit, Input, Renderer2 } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { ModalController } from '@ionic/angular';
import { NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer';

@Component({
  selector: 'app-report-viewer',
  templateUrl: './report-viewer.component.html',
  styleUrls: ['./report-viewer.component.scss'],
})
export class ReportViewerComponent implements OnInit, OnDestroy {
  @Input() file: any;
  @Input() exportType: any = 'pdf';
  @Input() reportType: any = 'Summary';
  @Input() assetName?: any = null;
  @Input() msn?: any = null;
  /** Choose behaviour when Add Comment is clicked while doc viewer is fullscreen:
   *  'exit-fullscreen' -> restore doc viewer then open nested modal (default)
   *  'overlay'         -> keep doc viewer fullscreen and render nested modal above it
   */
  @Input() nestedModalBehavior: 'exit-fullscreen' | 'overlay' = 'overlay';

  // loading / embed state (your existing fields)
  isLoading = true;
  error: string | null = null;
  trustedUrl: any | null = null;
  pdfViewerUrl: any | null = null;
  fileExt: string | null = null;
  mimeType: string | null = null;
  objectUrl: string | null = null;
  excelHtml: string | null = null;
  mimeTypes: { [key: string]: string } = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
  };
  // resize & sizing
  minWidth = 1080;
  minHeight = 700;
  maxWidth = window.innerWidth - 40;
  maxHeight = window.innerHeight - 40;
  // track last non-fullscreen size so we can restore when exiting fullscreen
  lastWidth = 1080;
  lastHeight = 700;
  iframeHtml: SafeHtml;

  // topbar
  topBarHeight = 56;

  // fullscreen state
  isFullscreen = false;
  documentDownload = false;

  // modal DOM references
  private modalContentEl: HTMLElement | null = null;

  // resize internals
  private resizing = false;
  // private resizeDir: 'right' | 'bottom' | 'corner' | null = null;
  private startX = 0;
  private startY = 0;
  private startW = 0;
  private startH = 0;

  // embed timeout fields (keep your previous values/constants)
  private embedLoadTimeout: any = null;
  private EMBED_TIMEOUT_MS = 20000;
  private resizeDir: string | null = null;
  private startLeft = 0;
  private startTop = 0;
  // custom backdrop reference (when using overlay behavior)
  private customBackdropEl: HTMLElement | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private elRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private modalController: ModalController,
    private pdfService: NgxExtendedPdfViewerService
  ) {}

  ngOnInit() {
    // find modal content element (the visual wrapper)
    (pdfMake as any).vfs = pdfFonts.pdfMake.vfs;
    this.modalContentEl = this.findModalContentElement();
    if (!this.modalContentEl) {
      // try again shortly — ionic sometimes inserts after init
      setTimeout(() => {
        this.modalContentEl = this.findModalContentElement();
        if (this.modalContentEl) this.applyInitialSizing();
      }, 60);
    } else {
      this.applyInitialSizing();
    }
    if (this.reportType === 'Summary') {
      // start loading doc if iframeSrc provided
      pdfMake.createPdf(this.file).getBlob((blob: Blob) => {
        this.loadPdfInViewer(blob);
      });
    } else {
      const blob = new Blob([this.file], {
        type:
          this.exportType === 'excel'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/pdf',
      });

      // 1) Generate object URL
      const url = URL.createObjectURL(blob);
      this.pdfViewerUrl = url;
      // 2) Sanitize for Angular
      this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

      // 3) Pass extension for viewer
      this.fileExt = this.exportType === 'excel' ? 'xlsx' : 'pdf';
      this.isLoading = false;
      this.error = null;
    }

    // fullscreen change listener
    document.addEventListener('fullscreenchange', this.onFullscreenChangeBound);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChangeBound);
    document.addEventListener('mozfullscreenchange', this.onFullscreenChangeBound);
    document.addEventListener('MSFullscreenChange', this.onFullscreenChangeBound);
  }

  ngOnDestroy() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChangeBound);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChangeBound);
    document.removeEventListener('mozfullscreenchange', this.onFullscreenChangeBound);
    document.removeEventListener('MSFullscreenChange', this.onFullscreenChangeBound);
    // cleanup object url
    if (this.objectUrl) {
      try {
        URL.revokeObjectURL(this.objectUrl);
      } catch {}
      this.objectUrl = null;
    }
    this.removeCustomBackdrop();
    this.clearEmbedTimeout();
  }

  // ---------------- sizing helpers ----------------
  private applyInitialSizing() {
    // If modal wrapper exists and no explicit size, apply defaults
    this.setModalSize(this.lastWidth, this.lastHeight);
    // set CSS var so inner calc can use topbar height
    this.modalContentEl!.style.setProperty('--top-bar-height', `${this.topBarHeight}px`);
  }

  private setModalSize(w: number, h: number) {
    // clamp
    const W = Math.max(this.minWidth, Math.min(this.maxWidth, Math.round(w)));
    const H = Math.max(this.minHeight, Math.min(this.maxHeight, Math.round(h)));
    this.lastWidth = W;
    this.lastHeight = H;
    // if modal element present, set inline styles to control its size
    if (this.modalContentEl) {
      // where to set size depends on how Ionic wraps the modal. We'll try a couple of targets:
      const targets: (HTMLElement | null)[] = [
        this.modalContentEl,
        this.modalContentEl.querySelector('.modal-wrapper') as HTMLElement | null,
        this.modalContentEl.querySelector('.modal-page') as HTMLElement | null,
      ];
      for (const t of targets) {
        if (!t) continue;
        t.style.setProperty('width', `${W}px`, 'important');
        t.style.setProperty('height', `${H}px`, 'important');
        t.style.setProperty('max-width', '100%', 'important');
      }
    }
  }

  private restoreToLastSize() {
    this.setModalSize(this.lastWidth, this.lastHeight);
    // remove fullscreen fallback class if present
    if (this.modalContentEl) this.renderer.removeClass(this.modalContentEl, 'is-fullscreen');
    this.isFullscreen = false;
  }

  // ---------------- drag resize handlers ----------------
  // start resizing (called from template via (mousedown)/(touchstart))
  startResize(ev: MouseEvent | TouchEvent, dir: string) {
    ev.preventDefault();
    this.resizing = true;
    this.resizeDir = dir;

    const rect = this.modalContentEl?.getBoundingClientRect();
    if (!rect) return;

    this.startX = this.getClientX(ev);
    this.startY = this.getClientY(ev);
    this.startW = rect.width;
    this.startH = rect.height;
    this.startLeft = rect.left;
    this.startTop = rect.top;

    window.addEventListener('mousemove', this.onPointerMoveBound);
    window.addEventListener('mouseup', this.stopResizeBound);
    window.addEventListener('touchmove', this.onPointerMoveBound, { passive: false });
    window.addEventListener('touchend', this.stopResizeBound);
    document.body.style.userSelect = 'none';
  }

  private getClientX(e: MouseEvent | TouchEvent) {
    return (e as TouchEvent).touches ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
  }
  private getClientY(e: MouseEvent | TouchEvent) {
    return (e as TouchEvent).touches ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;
  }

  private onPointerMoveBound = this.onPointerMove.bind(this);
  private stopResizeBound = this.stopResize.bind(this);

  private onPointerMove(e: MouseEvent | TouchEvent) {
    if (!this.resizing || !this.modalContentEl) return;
    e.preventDefault();

    const cx = this.getClientX(e);
    const cy = this.getClientY(e);
    const dx = cx - this.startX;
    const dy = cy - this.startY;

    let newW = this.startW;
    let newH = this.startH;
    let newLeft = this.startLeft;
    let newTop = this.startTop;

    if (this.resizeDir?.includes('right')) newW = this.startW + dx;
    if (this.resizeDir?.includes('left')) {
      newW = this.startW - dx;
      newLeft = this.startLeft + dx;
    }
    if (this.resizeDir?.includes('bottom')) newH = this.startH + dy;
    if (this.resizeDir?.includes('top')) {
      newH = this.startH - dy;
      newTop = this.startTop + dy;
    }

    newW = Math.max(this.minWidth, Math.min(this.maxWidth, newW));
    newH = Math.max(this.minHeight, Math.min(this.maxHeight, newH));
    newLeft = Math.min(Math.max(0, newLeft), window.innerWidth - newW);
    newTop = Math.min(Math.max(0, newTop), window.innerHeight - newH);

    this.modalContentEl.style.width = `${newW}px`;
    this.modalContentEl.style.height = `${newH}px`;
    this.modalContentEl.style.left = `${newLeft}px`;
    this.modalContentEl.style.top = `${newTop}px`;
  }

  private stopResize() {
    if (!this.resizing) return;
    this.resizing = false;
    this.resizeDir = null;
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', this.onPointerMoveBound);
    window.removeEventListener('mouseup', this.stopResizeBound);
    window.removeEventListener('touchmove', this.onPointerMoveBound);
    window.removeEventListener('touchend', this.stopResizeBound);
  }

  /** Insert backdrop DOM node immediately before the nested modal element */
  private insertBackdropBeforeModal(modalEl: HTMLElement) {
    // remove any existing custom backdrop
    this.removeCustomBackdrop();

    // compute highest z-index excluding the modalEl
    const baseZ = this.getHighestZIndexExcluding(modalEl);

    // backdrop will sit above existing overlays (baseZ+1), modal will be baseZ+2
    const backdropZ = baseZ + 1;
    const modalZ = baseZ + 2;

    // create backdrop element
    const backdrop = document.createElement('div');
    backdrop.id = 'custom-nested-backdrop';
    backdrop.className = 'custom-nested-backdrop';
    Object.assign(backdrop.style, {
      position: 'fixed',
      inset: '0',
      background: 'rgba(0,0,0,0.45)',
      backdropFilter: 'blur(2px)',
      zIndex: String(backdropZ),
      pointerEvents: 'auto',
    });

    // Insert before the modal element in DOM so stacking order is correct
    // (backdrop will be under the modal because it appears earlier in DOM)
    modalEl.parentNode?.insertBefore(backdrop, modalEl);

    // store for later removal
    (window as any).__customNestedBackdrop = backdrop;

    // prevent body scroll while nested modal open
    document.body.style.overflow = 'hidden';

    // make sure that modal and its inner wrapper have a higher z-index
    try {
      modalEl.style.setProperty('z-index', String(modalZ), 'important');
      const inner =
        (modalEl.shadowRoot?.querySelector('.modal-wrapper') as HTMLElement | null) ||
        (modalEl.querySelector('.modal-wrapper') as HTMLElement | null);
      if (inner) inner.style.setProperty('z-index', String(modalZ), 'important');
    } catch (e) {
      /* ignore */
    }

    // hide Ionic's own backdrop inside the nested modal (if present)
    try {
      const ionBackdrop =
        (modalEl.shadowRoot?.querySelector('ion-backdrop') as HTMLElement | null) ||
        (modalEl.querySelector('ion-backdrop') as HTMLElement | null);
      if (ionBackdrop) ionBackdrop.style.setProperty('display', 'none', 'important');
    } catch (e) {}
  }

  /** Try to adjust z-index of nested modal after present */
  private bringModalAboveBackdrop(modalEl: HTMLElement) {
    const backdrop = (window as any).__customNestedBackdrop as HTMLElement | null;
    if (!backdrop) {
      // fallback: create one at highest level
      this.insertBackdropBeforeModal(modalEl);
      return;
    }
    const backdropZ = parseInt(backdrop.style.zIndex || '10000', 10) || 10000;
    const modalZ = backdropZ + 1;
    try {
      modalEl.style.setProperty('z-index', String(modalZ), 'important');
      const inner =
        (modalEl.shadowRoot?.querySelector('.modal-wrapper') as HTMLElement | null) ||
        (modalEl.querySelector('.modal-wrapper') as HTMLElement | null);
      if (inner) inner.style.setProperty('z-index', String(modalZ), 'important');
    } catch (e) {
      /* ignore */
    }
  }

  /** Simple fallback backdrop if modal element couldn't be located */
  private createSimpleBackdrop() {
    this.removeCustomBackdrop();
    const backdrop = document.createElement('div');
    backdrop.id = 'custom-nested-backdrop';
    backdrop.className = 'custom-nested-backdrop';
    Object.assign(backdrop.style, {
      position: 'fixed',
      inset: '0',
      background: 'rgba(0,0,0,0.45)',
      zIndex: String(10000),
      pointerEvents: 'auto',
    });
    document.body.appendChild(backdrop);
    document.body.style.overflow = 'hidden';
    (window as any).__customNestedBackdrop = backdrop;
  }

  /** Remove our custom backdrop and restore scroll */
  private removeCustomBackdrop() {
    const d = document.getElementById('custom-nested-backdrop');
    if (d) d.remove();
    document.body.style.overflow = '';
    delete (window as any).__customNestedBackdrop;
  }

  /** Return the highest numeric z-index in the document excluding optional element */
  private getHighestZIndexExcluding(excludeEl?: Element | null): number {
    const nodes = Array.from(document.querySelectorAll('body *')) as HTMLElement[];
    let max = 1000;
    for (const el of nodes) {
      if (!el.offsetParent) continue; // skip hidden elements
      if (excludeEl && (excludeEl === el || excludeEl.contains(el))) continue;
      const z = window.getComputedStyle(el).zIndex;
      const zi = parseInt(String(z), 10);
      if (!Number.isNaN(zi)) max = Math.max(max, zi);
    }
    return max;
  }

  // ---------------- Fullscreen API ----------------
  private onFullscreenChangeBound = () => this.onFullscreenChange();
  private async enterFullscreenOn(el: HTMLElement) {
    try {
      const req =
        (el as any).requestFullscreen ||
        (el as any).webkitRequestFullscreen ||
        (el as any).mozRequestFullScreen ||
        (el as any).msRequestFullscreen;
      if (req) {
        await req.call(el);
        this.renderer.addClass(el, 'is-fullscreen');
        this.isFullscreen = true;
        return true;
      }
    } catch (err) {
      console.warn('Fullscreen API failed', err);
    }
    return false;
  }

  async toggleFullScreen() {
    this.modalContentEl = this.modalContentEl || this.findModalContentElement();
    const targetEl = this.modalContentEl || this.elRef.nativeElement;
    if (!targetEl) return;

    const inFs = !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
    if (inFs) {
      try {
        if ((document as any).exitFullscreen) await (document as any).exitFullscreen();
        else if ((document as any).webkitExitFullscreen) await (document as any).webkitExitFullscreen();
      } catch {}
      // remove fallback class and restore size
      if (this.modalContentEl) this.renderer.removeClass(this.modalContentEl, 'is-fullscreen');
      this.isFullscreen = false;
      this.restoreToLastSize();
      return;
    }

    // entering fullscreen
    // save current size
    this.lastWidth = this.lastWidth || 1000;
    this.lastHeight = this.lastHeight || 700;

    const ok = await this.enterFullscreenOn(targetEl);
    if (!ok) {
      // fallback: add css class
      if (this.modalContentEl) this.renderer.addClass(this.modalContentEl, 'is-fullscreen');
      this.isFullscreen = true;
      // make sure modal fills viewport via CSS rules
    }
  }

  private onFullscreenChange() {
    const fsEl =
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement;
    this.isFullscreen = !!fsEl;
    // if leaving fullscreen, restore sizes
    if (!this.isFullscreen) this.restoreToLastSize();
  }

  private findModalContentElement(): HTMLElement | null {
    const rootEl = document.querySelector('ion-modal.document-viewer') || document.querySelector('.document-viewer');
    if (!rootEl) return null;

    const tryShadow = (sel: string) => {
      try {
        if ((rootEl as any).shadowRoot) {
          return (rootEl as any).shadowRoot.querySelector(sel) as HTMLElement | null;
        }
      } catch {}
      return null;
    };

    const selectors = ['.modal-wrapper', '.modal-page', '.modal-content', '.modal', '.modal-host', '.ion-page'];
    for (const sel of selectors) {
      const found = tryShadow(sel) || (rootEl.querySelector ? rootEl.querySelector(sel) : null);
      if (found) return found as HTMLElement;
    }

    const firstChild = rootEl.firstElementChild as HTMLElement | null;
    if (firstChild) return firstChild;
    return null;
  }

  private cleanup() {
    if (this.objectUrl) {
      try {
        URL.revokeObjectURL(this.objectUrl);
      } catch {}
      this.objectUrl = null;
    }
  }

  private startEmbedTimeout() {
    // Keep loader visible until embed signals load or timeout fires
    this.clearEmbedTimeout();
    this.embedLoadTimeout = setTimeout(() => {
      if (this.isLoading) {
        this.isLoading = false;
        this.error = 'Preview timed out. Document may be blocked by CORS or not publicly accessible.';
        console.warn('Embed timed out.');
      }
    }, this.EMBED_TIMEOUT_MS);
  }

  private clearEmbedTimeout() {
    if (this.embedLoadTimeout) {
      clearTimeout(this.embedLoadTimeout);
      this.embedLoadTimeout = null;
    }
  }
  private escapeHtml(s: string) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  closeModal() {
    // dismiss the Ionic modal
    this.modalController.dismiss().catch(() => {
      /* ignore */
    });
  }

  onEmbedLoaded() {
    // Stop the timeout and hide loader after a tiny delay so content is visually ready
    this.clearEmbedTimeout();
    setTimeout(() => {
      this.isLoading = false;
    }, 120);
  }

  loadPdfInViewer(blob: Blob) {
    const url = URL.createObjectURL(blob);
    this.pdfViewerUrl = url
    this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.fileExt = 'pdf';
    this.mimeType = 'application/pdf';

    this.isLoading = false;
    this.error = null;
  }
  openOrDownloadFile() {
    const blob = new Blob([this.file], {
      type:
        this.exportType === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
    });

    const fileExtension = this.exportType === 'excel' ? 'xlsx' : 'pdf';
    const fileName = `${this.assetName
      .toLowerCase()
      .trim()
      .replace(/^./, (c) => c.toUpperCase())} History ${this.msn}`;

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    link.remove();
  }
  async downloadPdf() {
  // 1. Get the current document as a Blob
  const blob = await this.pdfService.getCurrentDocumentAsBlob();

  if (blob) {
    // 2. Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'document.pdf'; // Use [filenameForDownload] value if needed

    // 3. Trigger the browser's download action
    link.click();

    // Clean up
    URL.revokeObjectURL(url);
  }
}
}
